import * as yup from 'yup';
import _get from 'lodash/get';
import { APP_LANGUAGE } from 'commons/models/common';
import { BANNER_CTA_TYPE } from 'commons/models/banner.model';
import {
	Row,
	RenderConfig,
	Column,
	TextInput,
	SelectInput,
	AssetUploadInputOnS3,
	EMPTY_ARRAY,
	CASE,
	ASSET_TYPE,
	NumberInput,
	BOOLEAN_OPTIONS
} from 'commons-ui';

import { CTA_TYPE_OPTIONS, CTA_VALUE_OPTIONS, NOTICE_TYPE_OPTIONS } from './constants';

export const setValueUndefined = (props: any) => {
	const { name, setValue } = props;
	setValue(name, undefined);
};

export const ctaValueDependencyResolver = (props: any, dependencyValues: any) => {
	const ctaType = _get(dependencyValues, '0');
	const { InputComponent } = props;
	const { disabled } = props;

	if ((ctaType === undefined || ctaType === null) && !disabled) {
		setValueUndefined(props);
		return { disabled: true };
	}
	if (ctaType === BANNER_CTA_TYPE.IN_APP && InputComponent !== SelectInput) {
		return { InputComponent: SelectInput, disabled: false };
	}
	if (InputComponent !== TextInput) {
		return { InputComponent: TextInput, disabled: false };
	}

	return null;
};

export const BASE_FORM_FIELD_KEYS = {
	NAME: 'name',
	TYPE: 'type',
	CARD_BG: 'cardBg',
	CARD_BG_URL: 'cardBgUrl',
	CARD_BG_COLOR: 'cardBgColor',
	IS_CROSSABLE: 'isCrossable',
	CTA: 'cta',
	CTA_TYPE: 'ctaType',
	CTA_VALUE: 'ctaValue',
	MAX_APP_VERSION: 'maxAppVersion',
	MIN_APP_VERSION: 'minAppVersion',
	ENABLED: 'enabled',
	EXPIRY: 'expiry',
	REPEAT: 'repeat',
	LIFETIME: 'lifetime',
	PRIORITY: 'priority',
	VERSION: 'version',
	IS_ARCHIVED: 'isArchived',
	VALID_FROM: 'validFrom',
	VALID_TILL: 'validTill',
	ABEXPERIMENTS: 'abExperiments',
	INCLUSIVE_SEGMENTS: 'inclusiveSegments',
	EXCLUSIVE_SEGMENTS: 'exclusiveSegments',
	MIN_GAMES: 'minGames'
};

export const NOTICE_FORM_FIELD_KEYS = {
	TITLE: 'title',
	TITLE_TEXT: 'titleText',
	TITLE_EN_TEXT: 'titleEnText',
	TITLE_HI_TEXT: 'titleHiText',
	TITLE_COLOR: 'titleColor',
	SUBTITLE: 'subtitle',
	SUBTITLE_TEXT: 'subtitleText',
	SUBTITLE_EN_TEXT: 'subtitleEnText',
	SUBTITLE_HI_TEXT: 'subtitleHiText',
	SUBTITLE_COLOR: 'subtitleColor',
	IMAGE_URL: 'imageUrl'
};

export const baseValidationSchema = yup.object().shape({
	name: yup
		.string()
		.required('Name is a required field')
		.matches(/^[a-zA-Z][\w+]{2,30}$/i, 'Name can contain only A-Z letters, numbers or underscore(_)'),
	type: yup.string().required('Type is a required field'),
	enabled: yup.boolean().required(),
	cardBg: yup.object().shape(
		{
			color: yup.string().matches(/^#[0-9a-f]{6}$/i, 'Color code should be heaxdecimal'),
			url: yup.string().when('color', {
				is: (color: string) => !color || color.length === 0,
				then: yup.string().required('At least one of the fields is required')
			})
		},
		[['color', 'url']]
	),
	isCrossable: yup.boolean().required(),
	isArchived: yup.boolean().optional().default(false),
	version: yup.number().positive().integer().optional(),
	validFrom: yup.string().required(),
	validTill: yup.string().required(),
	abExperiments: yup
		.string()
		.matches(/^([a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)*(?:,|$))*$/i, 'Add comma seperated Ab as "[expname-bucketname]"')
		.optional(),
	repeat: yup.number().min(0, 'Repeat should be greater than equal to 0').integer('Number should be an integer').required(),
	expiry: yup.number().min(0, 'Expiry should be greater than equal to 0').integer('Number should be an integer').required(),
	lifetime: yup
		.number()
		.min(0, 'Lifetime Count should be greater than equal to 0')
		.integer('Number should be an integer')
		.required(),
	priority: yup.number().min(0, 'Priority should be greater than equal to 0').integer('Number should be an integer').required(),
	inclusiveSegments: yup
		.string()
		.default('')
		.matches(/^([\w\s]+(,[\w\s]+)*)?$/i, 'Segments can only contain comma separated values')
		.optional(),
	exclusiveSegments: yup
		.string()
		.default('')
		.matches(/^([\w\s]+(,[\w\s]+)*)?$/i, 'Segments can only contain comma separated values')
		.optional(),
	minGames: yup
		.number()
		.min(0, 'Minimum Games should be greater than equal to 0')
		.integer('Number should be an integer')
		.required(),
	minAppVersion: yup
		.number()
		.min(0, 'Minimum App Version should be greater than equal to 0')
		.integer('Number should be an integer')
		.required(),
	maxAppVersion: yup
		.number()
		.min(0, 'Maximum App Version should be greater than equal to 0')
		.integer('Number should be an integer')
		.required()
});

export const noticeValidationSchema = baseValidationSchema.shape({
	title: yup.object().shape({
		text: yup.object().shape({
			[APP_LANGUAGE.ENGLISH]: yup.string().required('English Text is a required field'),
			[APP_LANGUAGE.HINDI]: yup.string().optional()
		}),
		color: yup
			.string()
			.required('Title Color is a required field')
			.matches(/^#[0-9a-f]{6}$/i, 'Color code should be heaxdecimal')
	}),
	subtitle: yup.object().shape({
		text: yup.object().shape({
			[APP_LANGUAGE.ENGLISH]: yup.string().required('English Text is a required field'),
			[APP_LANGUAGE.HINDI]: yup.string().optional()
		}),
		color: yup
			.string()
			.required('Subtitle Color is a required field')
			.matches(/^#[0-9a-f]{6}$/i, 'Color code should be heaxdecimal')
	}),
	cta: yup
		.object()
		.shape({
			type: yup.string(),
			value: yup.mixed().when('type', {
				is: (type: string) => type !== undefined && type != null,
				then: yup.string().required('value is required when type is present'),
				otherwise: yup.mixed().notRequired()
			})
		})
		.optional(),
	imageUrl: yup.string().url().required('Image Url is a required field')
});

export const BASE_FORM_FIELD_CONFIG = {
	[BASE_FORM_FIELD_KEYS.NAME]: new RenderConfig()
		.setName('name')
		.setLabel('Name')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: false }, letterCase: CASE.UPPERCASE, required: true }),
	[BASE_FORM_FIELD_KEYS.TYPE]: new RenderConfig()
		.setName('type')
		.setLabel('Type')
		.setRenderer(SelectInput)
		.setRenderProps({ isEditable: { create: true, edit: false }, options: NOTICE_TYPE_OPTIONS, required: true }),
	[BASE_FORM_FIELD_KEYS.CARD_BG]: new RenderConfig()
		.setName('cardBg')
		.setLabel('Card Background')
		.setRenderProps({ required: true }),
	[BASE_FORM_FIELD_KEYS.CARD_BG_COLOR]: new RenderConfig()
		.setName('color')
		.setLabel('Card Background Color')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[BASE_FORM_FIELD_KEYS.CARD_BG_URL]: new RenderConfig()
		.setName('url')
		.setLabel('Card Background URL')
		.setRenderer(AssetUploadInputOnS3)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			assetType: ASSET_TYPE.IMAGE,
			maxFileSizeMB: 0.1
		}),
	[BASE_FORM_FIELD_KEYS.IS_CROSSABLE]: new RenderConfig()
		.setName('isCrossable')
		.setLabel('Is Crossable')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: BOOLEAN_OPTIONS,
			required: true
		}),
	[BASE_FORM_FIELD_KEYS.CTA]: new RenderConfig().setName('cta').setLabel('CTA').setRenderProps({ required: false }),
	[BASE_FORM_FIELD_KEYS.CTA_TYPE]: new RenderConfig()
		.setName('type')
		.setLabel('Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: CTA_TYPE_OPTIONS,
			required: false
		}),
	[BASE_FORM_FIELD_KEYS.CTA_VALUE]: new RenderConfig()
		.setName('value')
		.setLabel('Value')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			options: CTA_VALUE_OPTIONS,
			dependencies: ['cta.type'],
			dependencyResolver: ctaValueDependencyResolver
		}),
	[BASE_FORM_FIELD_KEYS.ENABLED]: new RenderConfig()
		.setName('enabled')
		.setLabel('Enabled')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: BOOLEAN_OPTIONS,
			required: true
		}),

	[BASE_FORM_FIELD_KEYS.EXPIRY]: new RenderConfig()
		.setName('expiry')
		.setLabel('Expiry')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),

	[BASE_FORM_FIELD_KEYS.REPEAT]: new RenderConfig()
		.setName('repeat')
		.setLabel('Repeat')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),

	[BASE_FORM_FIELD_KEYS.LIFETIME]: new RenderConfig()
		.setName('lifetime')
		.setLabel('Lifetime')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),

	[BASE_FORM_FIELD_KEYS.INCLUSIVE_SEGMENTS]: new RenderConfig()
		.setName('inclusiveSegments')
		.setLabel('Inclusive Segments')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false,
			default: ''
		}),

	[BASE_FORM_FIELD_KEYS.EXCLUSIVE_SEGMENTS]: new RenderConfig()
		.setName('exclusiveSegments')
		.setLabel('Exclusive Segments')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false,
			default: ''
		}),

	[BASE_FORM_FIELD_KEYS.PRIORITY]: new RenderConfig()
		.setName('priority')
		.setLabel('Priority')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),

	[BASE_FORM_FIELD_KEYS.MIN_GAMES]: new RenderConfig()
		.setName('minGames')
		.setLabel('Minimum Games')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[BASE_FORM_FIELD_KEYS.VALID_FROM]: new RenderConfig()
		.setName('validFrom')
		.setLabel('Valid From')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			inputType: 'datetime-local',
			format: 'yyyy-dd-mm HH:MM:ss',
			required: true
		}),
	[BASE_FORM_FIELD_KEYS.VALID_TILL]: new RenderConfig()
		.setName('validTill')
		.setLabel('Valid Till')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			inputType: 'datetime-local',
			format: 'yyyy-dd-mm HH:MM:ss',
			required: false
		}),
	[BASE_FORM_FIELD_KEYS.VERSION]: new RenderConfig()
		.setName('version')
		.setLabel('Version')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false
		}),
	[BASE_FORM_FIELD_KEYS.IS_ARCHIVED]: new RenderConfig()
		.setName('isArchived')
		.setLabel('Archived')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: BOOLEAN_OPTIONS,
			required: true
		}),
	[BASE_FORM_FIELD_KEYS.ABEXPERIMENTS]: new RenderConfig()
		.setName('abExperiments')
		.setLabel('Supported AB Experiments')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false
		}),
	[BASE_FORM_FIELD_KEYS.MAX_APP_VERSION]: new RenderConfig()
		.setName('minAppVersion')
		.setLabel('Min App Version Supported')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[BASE_FORM_FIELD_KEYS.MIN_APP_VERSION]: new RenderConfig()
		.setName('maxAppVersion')
		.setLabel('Max App Version Supported')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		})
};

export const NOTICE_FORM_FIELD_CONFIG = {
	...BASE_FORM_FIELD_CONFIG,
	[NOTICE_FORM_FIELD_KEYS.TITLE]: new RenderConfig().setName('title').setLabel('Title').setRenderProps({ required: true }),
	[NOTICE_FORM_FIELD_KEYS.TITLE_TEXT]: new RenderConfig().setName('text').setLabel('Title Text').setRenderProps({ required: true }),
	[NOTICE_FORM_FIELD_KEYS.TITLE_EN_TEXT]: new RenderConfig()
		.setName(APP_LANGUAGE.ENGLISH)
		.setLabel('English')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[NOTICE_FORM_FIELD_KEYS.TITLE_HI_TEXT]: new RenderConfig()
		.setName(APP_LANGUAGE.HINDI)
		.setLabel('Hindi')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: false }),
	[NOTICE_FORM_FIELD_KEYS.TITLE_COLOR]: new RenderConfig()
		.setName('color')
		.setLabel('Color')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[NOTICE_FORM_FIELD_KEYS.SUBTITLE]: new RenderConfig().setName('subtitle').setLabel('Subtitle').setRenderProps({ required: true }),
	[NOTICE_FORM_FIELD_KEYS.SUBTITLE_TEXT]: new RenderConfig()
		.setName('text')
		.setLabel('Subtitle Text')
		.setRenderProps({ required: true }),
	[NOTICE_FORM_FIELD_KEYS.SUBTITLE_EN_TEXT]: new RenderConfig()
		.setName(APP_LANGUAGE.ENGLISH)
		.setLabel('English')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true }),
	[NOTICE_FORM_FIELD_KEYS.SUBTITLE_HI_TEXT]: new RenderConfig()
		.setName(APP_LANGUAGE.HINDI)
		.setLabel('Hindi')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: false }),
	[NOTICE_FORM_FIELD_KEYS.SUBTITLE_COLOR]: new RenderConfig()
		.setName('color')
		.setLabel('Color')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[NOTICE_FORM_FIELD_KEYS.IMAGE_URL]: new RenderConfig()
		.setName('imageUrl')
		.setLabel('Image')
		.setRenderer(AssetUploadInputOnS3)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			assetType: ASSET_TYPE.IMAGE,
			maxFileSizeMB: 0.1
		})
};

export const FORM_LAYOUT_CONFIG = [
	new Row(null, EMPTY_ARRAY, [new Column([new Row(BASE_FORM_FIELD_KEYS.NAME)])]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(BASE_FORM_FIELD_KEYS.ENABLED)]),
		new Column([new Row(BASE_FORM_FIELD_KEYS.TYPE)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(BASE_FORM_FIELD_KEYS.IS_ARCHIVED)]),
		new Column([new Row(BASE_FORM_FIELD_KEYS.VERSION)])
	]),
	new Row(NOTICE_FORM_FIELD_KEYS.TITLE, EMPTY_ARRAY, [
		new Column([
			new Row(NOTICE_FORM_FIELD_KEYS.TITLE_TEXT, EMPTY_ARRAY, [
				new Column([new Row(NOTICE_FORM_FIELD_KEYS.TITLE_EN_TEXT)]),
				new Column([new Row(NOTICE_FORM_FIELD_KEYS.TITLE_HI_TEXT)])
			]),
			new Row(null, EMPTY_ARRAY, [new Column([new Row(NOTICE_FORM_FIELD_KEYS.TITLE_COLOR)])])
		])
	]),
	new Row(NOTICE_FORM_FIELD_KEYS.SUBTITLE, EMPTY_ARRAY, [
		new Column([
			new Row(NOTICE_FORM_FIELD_KEYS.SUBTITLE_TEXT, EMPTY_ARRAY, [
				new Column([new Row(NOTICE_FORM_FIELD_KEYS.SUBTITLE_EN_TEXT)]),
				new Column([new Row(NOTICE_FORM_FIELD_KEYS.SUBTITLE_HI_TEXT)])
			]),
			new Row(null, EMPTY_ARRAY, [new Column([new Row(NOTICE_FORM_FIELD_KEYS.SUBTITLE_COLOR)])])
		])
	]),
	new Row(BASE_FORM_FIELD_KEYS.CARD_BG, EMPTY_ARRAY, [
		new Column([new Row(BASE_FORM_FIELD_KEYS.CARD_BG_COLOR)]),
		new Column([new Row(BASE_FORM_FIELD_KEYS.CARD_BG_URL)])
	]),
	new Row(NOTICE_FORM_FIELD_KEYS.IMAGE_URL),
	new Row(BASE_FORM_FIELD_KEYS.IS_CROSSABLE),
	new Row(BASE_FORM_FIELD_KEYS.CTA, EMPTY_ARRAY, [
		new Column([new Row(BASE_FORM_FIELD_KEYS.CTA_TYPE)]),
		new Column([new Row(BASE_FORM_FIELD_KEYS.CTA_VALUE)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(BASE_FORM_FIELD_KEYS.PRIORITY)]),
		new Column([new Row(BASE_FORM_FIELD_KEYS.MIN_GAMES)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(BASE_FORM_FIELD_KEYS.REPEAT)]),
		new Column([new Row(BASE_FORM_FIELD_KEYS.EXPIRY)])
	]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(BASE_FORM_FIELD_KEYS.LIFETIME)])]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(BASE_FORM_FIELD_KEYS.VALID_FROM)]),
		new Column([new Row(BASE_FORM_FIELD_KEYS.VALID_TILL)])
	]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(BASE_FORM_FIELD_KEYS.ABEXPERIMENTS)])]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(BASE_FORM_FIELD_KEYS.INCLUSIVE_SEGMENTS)]),
		new Column([new Row(BASE_FORM_FIELD_KEYS.EXCLUSIVE_SEGMENTS)])
	]),

	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(BASE_FORM_FIELD_KEYS.MAX_APP_VERSION)]),
		new Column([new Row(BASE_FORM_FIELD_KEYS.MIN_APP_VERSION)])
	])
];
